import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  InputBase,
  Autocomplete as MuiAutocomplete,
  Popper,
  PopperProps,
  styled,
} from '@mui/material'
import { ReferencedEntity } from '../../../../domain/value-object/ReferencedEntity'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce } from '../../../hooks/useDebounce'
import { MenuItem, MenuItemText } from '../../menu'
import { ExpandMoreRounded, CloseRounded } from '@mui/icons-material'
import { colorPalette } from '../../../style/colorPalette'
import { useExpandableOptionGroups } from '../useExpandableOptionGroups'
import { useLatestSearch } from '../useLatestSearch'
import { Checkbox } from '../Checkbox'

export type RenderTagsOnMultiAutocomplete<Entity extends ReferencedEntity> = (
  value: Entity[],
  getTagProps: AutocompleteRenderGetTagProps
) => ReactNode
type MultiAutocompleteProps<Entity extends ReferencedEntity> = {
  value: Entity[]
  onChange?: (v: Entity[]) => void
  search: (searchText: string) => Promise<Entity[]>

  renderTags?: RenderTagsOnMultiAutocomplete<Entity>
  renderOption?: (v: Entity) => JSX.Element
  renderOptionRaw?: (
    // TODO Remove
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Entity,
    state: AutocompleteRenderOptionState
  ) => JSX.Element
  groupBy?: (option: Entity) => string
  // MUIのグルーピングは同じグループの候補が連続して並んでいることを前提とするため、
  // groupBy と組で渡す。
  sortOptions?: (options: Entity[]) => Entity[]
  defaultExpandedGroups?: string[]
}

export const MultiAutocomplete = <Entity extends ReferencedEntity>({
  value,
  onChange,
  search,
  renderTags,
  renderOption: _renderOption,
  renderOptionRaw,
  groupBy,
  sortOptions,
  defaultExpandedGroups,
}: MultiAutocompleteProps<Entity>) => {
  const [inputValue, setInputValue] = useState<string>('')
  const [fetchedOptions, setFetchedOptions] = useState<Entity[]>(value || [])
  const options = useMemo(
    () => (sortOptions ? sortOptions(fetchedOptions) : fetchedOptions),
    [fetchedOptions, sortOptions]
  )
  const [inputChangedBy, setInputChangedBy] =
    useState<AutocompleteInputChangeReason>('reset')
  const runSearch = useLatestSearch(search, setFetchedOptions)
  const onChangeValue = useCallback(
    (_, v: Entity[], reason: AutocompleteChangeReason) => {
      onChange && onChange(v)
      // 選択・選択解除するとMUIが入力欄を空に戻すが、その変更は入力起因ではないため再検索されない。
      // 絞り込んだままだと残りの候補が消えて見えるので、絞り込み中に限りここで解除する。
      // クリアボタンでは入力起因の検索が別に走るため対象外とする。
      const isInputReset =
        reason === 'selectOption' || reason === 'removeOption'
      if (isInputReset && inputValue) runSearch('')
    },
    [inputValue, onChange, runSearch]
  )
  const onChangeInput = useCallback(
    (_, value: string, reason: AutocompleteInputChangeReason) => {
      setInputValue(value)
      setInputChangedBy(reason)
    },
    []
  )

  const onFocus = useCallback(() => {
    runSearch('')
  }, [runSearch])
  const debouncedValue = useDebounce(inputValue, 300)
  useEffect(() => {
    if (['input', 'clear'].includes(inputChangedBy)) {
      runSearch(debouncedValue)
    }
  }, [debouncedValue, inputChangedBy, runSearch])
  const renderInput = useCallback((params: AutocompleteRenderInputParams) => {
    return <TextInput {...params.InputProps} inputProps={params.inputProps} />
  }, [])
  const renderOption = useCallback(
    (props, value: Entity, { selected }: AutocompleteRenderOptionState) => {
      return (
        <MenuItem {...props}>
          <Checkbox
            size="s"
            checked={selected}
            // 選択は候補のクリックで行うため、チェックボックス自体は表示専用とする
            disableRipple={true}
            inputProps={{ tabIndex: -1 }}
          />
          {_renderOption ? (
            _renderOption(value)
          ) : (
            <MenuItemText>{value.name}</MenuItemText>
          )}
        </MenuItem>
      )
    },
    [_renderOption]
  )
  const isOptionEqualToValue = useCallback((option: Entity, value: Entity) => {
    return option.uuid === value.uuid
  }, [])
  const getOptionLabel = useCallback((option: Entity) => {
    return option.name
  }, [])
  // インライン関数で渡すと再描画のたびに別コンポーネント扱いになり、リストボックスが
  // 再マウントされてグループの開閉ごとにスクロール位置が先頭に戻る。メモ化する。
  const PopperComponent = useMemo(
    () =>
      function MultiAutocompletePopper(props: PopperProps) {
        return <Popper {...props} placement="bottom-start" />
      },
    []
  )
  const selectedValues = useMemo(() => value || [], [value])
  const isSelected = useCallback(
    (option: Entity) =>
      selectedValues.some(selected => selected.uuid === option.uuid),
    [selectedValues]
  )
  const { renderGroup } = useExpandableOptionGroups({
    options,
    isSelected,
    groupBy,
    defaultExpandedGroups,
    isSearching: inputValue.trim().length > 0,
  })
  return (
    <MuiAutocomplete
      // Use sx props since making Autocomplete styled component takes a bit effort.
      sx={{ width: '100%' }}
      multiple={true}
      value={selectedValues}
      onChange={onChangeValue}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      renderInput={renderInput}
      inputValue={inputValue}
      onInputChange={onChangeInput}
      onFocus={onFocus}
      getOptionLabel={getOptionLabel}
      renderOption={renderOptionRaw || renderOption}
      groupBy={groupBy}
      renderGroup={renderGroup}
      disableCloseOnSelect={true}
      PopperComponent={PopperComponent}
      popupIcon={<PopupIcon />}
      clearIcon={<CloseIcon />}
      renderTags={renderTags}
      filterOptions={opts => opts}
    />
  )
}

const TextInput = styled(InputBase)({
  width: '100%',
  color: colorPalette.monotone[10],
})
const PopupIcon = styled(ExpandMoreRounded)({
  color: colorPalette.monotone[4],
})
const CloseIcon = styled(CloseRounded)({
  color: colorPalette.monotone[4],
  fontSize: '14px',
})
